import { useQuery } from "@tanstack/react-query";
import React, { useEffect } from "react";
import { RootStateOrAny, useSelector } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { getQuarterlyTableForDownloadData } from "../../../../api/brandPerformanceApi";
import { NoDataMsg } from "../../../../components/AppMessages";
import LoadingEllipsis from "../../../../components/Loading/LoadingEllipsis";
import BrandPerformanceTable from "../../../../components/Tables/BrandPerformanceTable";
import CompetitorSelect from "../competitorSelect";
import { getCuts, withMethodologyBreaks } from "./methodologyBreak";

interface Props extends RouteComponentProps {
  diff: number;
  onHandleRadioClick: any;
  nissanCompetitor: string;
  granularity: string;
}

const TableForDownloadQuarterly = withRouter((props: Props) => {
  const { history, onHandleRadioClick, nissanCompetitor, granularity } = props;
  const params = useSelector((state: RootStateOrAny) => state.parameters);

  const {
    data: quarterlyTableForDownloadData,
    isFetching: loadingQuarterlyTableForDownloadData,
    refetch: refetchTableForDownloadQuarterly,
  } = useQuery(["quarterlyTableData", granularity, history.location.search], () => getQuarterlyTableForDownloadData(params), {
    enabled: false,
  });

  // when the url changes, refetch the data
  useEffect(() => {
    refetchTableForDownloadQuarterly();
  }, [history.location.search]);

  // the backend response returns a dynamic date columns with the format of "yyyy-mm-dd" this helps to filter out the date columns
  const dateKeys =
    quarterlyTableForDownloadData?.length > 0
      ? Object.keys(quarterlyTableForDownloadData[0]).filter((key) => key.match(/^[FC]Y\d{2} [QW][1-4]$/))
      : [];

  // keys of the last comparable (pre-change) column(s); empty unless the BE flags a methodology break
  const cuts = getCuts(quarterlyTableForDownloadData);

  // Define dynamic date columns
  const dynamicColumns = dateKeys.map((dateKey) => ({
    id: dateKey,
    Header: dateKey,
    accessor: dateKey,
    Cell: (props: { value: number }) => {
      const { value } = props;
      // number is in thousands add commas
      const formattedValue = value?.toLocaleString();
      return formattedValue;
    },
  }));

  const staticColumns = [
    {
      id: "No",
      Header: `No`,
      accessor: "No",

      Cell: (props: { value: string }) => {
        const { value } = props;

        return value;
      },
    },
    {
      Header: "Country",
      accessor: `country`,
      id: "country",

      Cell: ({ value }: { value: number }) => {
        return value;
      },
    },
    {
      Header: "Data Category",
      accessor: `data_category`,
      id: "data_category`,",

      Cell: ({ value }: { value: number }) => {
        return value;
      },
    },
    {
      Header: "Category Detail",
      accessor: `category_detail`,
      id: "category_detail`,",

      Cell: ({ value }: { value: number }) => {
        return value;
      },
    },
    {
      Header: "Attribute",
      accessor: `attribute`,
      id: "attribute",
      Cell: ({ value }: { value: string }) => {
        return value;
      },
    },
    {
      Header: "Segment Name",
      accessor: `segment_name`,
      id: "segment_name`,",

      Cell: ({ value }: { value: number }) => {
        return value;
      },
    },
    {
      Header: "Nissan Brand/Model",
      accessor: `nissan_brand_or_model`,
      id: "nissan_brand_or_model`,",

      Cell: ({ value }: { value: number }) => {
        return value;
      },
    },
    {
      Header: "Comp Brand",
      accessor: `comp_brand`,
      id: "comp_brand`,",

      Cell: ({ value }: { value: number }) => {
        return value;
      },
    },
    {
      Header: "Comp Model",
      accessor: `comp_model`,
      id: "comp_model`,",

      Cell: ({ value }: { value: number }) => {
        return value;
      },
    },
  ];

  const columns = React.useMemo(
    () => [...staticColumns, ...withMethodologyBreaks(dynamicColumns, dateKeys, cuts)],
    [staticColumns, dynamicColumns, dateKeys, cuts]
  );

  return (
    <div className="table-download-content">
      <div className="primary_background ">
        {params?.data_category !== "NIM-SHOW-HACK" && (
          <div className="header" style={{ padding: "1.5em" }}>
            <div className="title_container">
              <h3>Table for data Download (PF/Image/NIM)</h3>
              <CompetitorSelect onHandleRadioClick={onHandleRadioClick} nissanCompetitor={nissanCompetitor} />
            </div>
          </div>
        )}
        <div className="table_wrapper">
          {params?.data_category !== "NIM-SHOW-HACK" ? (
            <div className="chart_content brand_perf_table" style={{ margin: "1em" }}>
              {quarterlyTableForDownloadData?.length && !loadingQuarterlyTableForDownloadData ? (
                <BrandPerformanceTable
                  isLoading={loadingQuarterlyTableForDownloadData}
                  columns={columns ?? []}
                  tableId="tableForDownloadMonthlyID"
                  data={quarterlyTableForDownloadData ?? []}
                  initialSortCol={{ id: "No", desc: false }}
                />
              ) : loadingQuarterlyTableForDownloadData ? (
                <LoadingEllipsis isLoading={loadingQuarterlyTableForDownloadData} />
              ) : (
                <NoDataMsg message="No data available" />
              )}
            </div>
          ) : (
            <div className="data_not_found_area primary_tile_background">
              This dashboard page is in development and will be available soon in CEDAR home
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
export default TableForDownloadQuarterly;
